import { Button, Icon, Skeleton, Text } from '@afterpaytouch/core'
import React, { FunctionComponent, useEffect, useId, useRef, useState } from 'react'
import { useUpdateCheckoutPromoMutation } from '@afterpaytouch/portal-api/consumer/checkout'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { setManualPromoCode, useAppDispatch, useAppRouter, useCheckoutState, useCheckoutToken } from '../../state'
import { Trans, useTranslation } from '../../utils/convergence'
import { useMoney } from '../Money/useMoney'
import styles from './style.module.css'

const MAX_ATTEMPTS = 5

type ViewState =
  | { kind: 'collapsed' }
  | { kind: 'expanded'; inputValue: string; errorCode: string | null }
  | { kind: 'applied'; promoCode: string; discountAmount: string }
  | { kind: 'appliedExpanded'; promoCode: string; discountAmount: string }

interface Props {
  testNameSpace?: string
}

function resolveErrorMessage(errorCode: string, t: (key: string) => string): string {
  switch (errorCode) {
    case 'CODENOTACCEPTED':
      return t('promoCode:errorInvalid')
    case 'ALREADY_ASSIGNED':
      return t('promoCode:errorAlreadyUsed')
    case 'RATE_LIMITED':
      return t('promoCode:tooManyAttempts')
    default:
      return t('promoCode:errorGeneric')
  }
}

export const PromoCodeInput: FunctionComponent<Props> = ({ testNameSpace = 'promo-code-input' }) => {
  const { t } = useTranslation()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutState = useCheckoutState()
  const dispatch = useAppDispatch()
  const token = useCheckoutToken()
  const { locale } = useAppRouter()

  const promotion = checkoutState.started?.promotion
  const persistedManualPromo = checkoutState.manualPromoCode
  // Use the checkout's consumer-amount currency as the stable formatter source so non-AU
  // discounts (e.g. USD for US merchants) format correctly even when no promo is applied yet.
  // Fall back to applied-promotion currency in case consumerAmount is unavailable.
  const checkoutCurrency = checkoutState.started?.consumerAmount?.currency
  const promotionCurrency = promotion?.discount?.currency

  const { formatMoney } = useMoney({ locale, currency: checkoutCurrency ?? promotionCurrency, decimalPlaces: 2 })

  // Live decision: should the component hide because the current `started.promotion` was
  // auto-applied at /start (auto-applied takes precedence — the auto-applied one's UI handles
  // its own display)?
  //
  // We disambiguate via `manualPromoCode`, a token-scoped marker recorded in checkout state on
  // every successful manual apply and cleared on remove. The promotion is treated as
  // user-applied iff its code AND the current checkout token both match the marker; otherwise
  // it's auto-applied. The token scope ensures a stale marker from a prior checkout (which can
  // be persisted across token boundaries by redux-persist) can't false-match a new auto-applied
  // /start promo with the same code.
  //
  // This is intentionally NOT a one-shot mount snapshot. If `started.promotion` is later
  // cleared externally (e.g., a reducer like `updatePreferredPayday` recomputes the order and
  // drops the auto-applied promo), the gate must re-open so the user can manually enter a
  // promo. Equally, if the user applies a code via this UI, the dispatch sets the marker so a
  // subsequent render with `started.promotion` populated still resolves to user-applied. The
  // local view state in `useEffect` below tracks the in-flight UI separately.
  const isAutoApplied = ((): boolean => {
    if (!promotion) return false
    if (!persistedManualPromo) return true
    if (persistedManualPromo.token !== token) return true
    return persistedManualPromo.code !== promotion.code
  })()

  const [updatePromo, { isLoading }] = useUpdateCheckoutPromoMutation()
  const [attemptCount, setAttemptCount] = useState(0)
  const [liveMessage, setLiveMessage] = useState('')
  const [view, setView] = useState<ViewState>(() => {
    if (promotion) {
      return {
        kind: 'applied',
        promoCode: promotion.code,
        discountAmount: formatMoney(promotion.discount.amount),
      }
    }
    return { kind: 'collapsed' }
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const isAttemptLimitReached = attemptCount >= MAX_ATTEMPTS

  // Reconcile view when checkout-state promotion changes externally (e.g. another reducer like
  // updatePreferredPayday recalculates `started.promotion`). Without this, the local view state
  // can go stale and drive users into invalid apply/remove actions based on outdated promo status.
  const externalPromoCode = promotion?.code
  const externalDiscountAmount = promotion?.discount?.amount
  useEffect(() => {
    if (externalPromoCode && externalDiscountAmount) {
      setView((prev) => {
        if ((prev.kind === 'applied' || prev.kind === 'appliedExpanded') && prev.promoCode === externalPromoCode) {
          return prev
        }
        return {
          kind: 'applied',
          promoCode: externalPromoCode,
          discountAmount: formatMoney(externalDiscountAmount),
        }
      })
    } else {
      setView((prev) => {
        if (prev.kind === 'applied' || prev.kind === 'appliedExpanded') {
          return { kind: 'collapsed' }
        }
        return prev
      })
    }
    // formatMoney is intentionally excluded — it's stable for the life of the component
    // and including it can trigger spurious reconciliations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPromoCode, externalDiscountAmount])

  useEffect(() => {
    if (view.kind === 'expanded') {
      inputRef.current?.focus()
    }
  }, [view.kind])

  const handleToggle = (): void => {
    if (view.kind === 'collapsed') {
      setView({ kind: 'expanded', inputValue: '', errorCode: null })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'expanded') {
      setView({ kind: 'collapsed' })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
      return
    }
    if (view.kind === 'applied') {
      setView({ kind: 'appliedExpanded', promoCode: view.promoCode, discountAmount: view.discountAmount })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'expanded' })
      return
    }
    if (view.kind === 'appliedExpanded') {
      setView({ kind: 'applied', promoCode: view.promoCode, discountAmount: view.discountAmount })
      logEvent(TrackingEvent.PRESSED_PROMO_CODE_TOGGLE, { action: 'collapsed' })
    }
  }

  const handleApply = async (): Promise<void> => {
    if (view.kind !== 'expanded') return
    // Defense in depth: even though the Apply button is aria-disabled when the cap is reached,
    // it remains keyboard-reachable so screen-reader users can focus it. Short-circuit here so
    // we don't fire additional apply requests / analytics events past the session cap.
    if (isAttemptLimitReached) return
    // Same belt + suspenders for the error window: the button is disabled but Enter-key
    // submission via onKeyDown would otherwise bypass it and re-fire the same failing request.
    // handleInputChange clears errorCode on edit, so this only blocks re-submitting the same
    // already-rejected input.
    if (view.errorCode) return
    const trimmedCode = view.inputValue.trim().toUpperCase()
    if (!trimmedCode) return

    const nextAttempt = attemptCount + 1
    setAttemptCount(nextAttempt)
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_APPLY, { attemptNumber: nextAttempt })

    try {
      const result = await updatePromo({ token, request: { action: 'APPLY', promoCode: trimmedCode } }).unwrap()
      const applied = result.promotion
      if (!applied) return
      const discountAmount = formatMoney(applied.discount.amount)
      logEvent(TrackingEvent.PROMO_CODE_APPLIED, { promoCode: trimmedCode, discountAmount })
      // Mark the source so a remount or page reload can distinguish this user-applied promo
      // from one that came back auto-applied at /start. Token-scoped so a stale marker from a
      // prior checkout can't misclassify a new auto-applied promo with the same code.
      dispatch(setManualPromoCode({ token, code: applied.code }))
      setLiveMessage(t('promoCode:applied'))
      setView({ kind: 'applied', promoCode: applied.code, discountAmount })
    } catch (err: unknown) {
      const error = err as { data?: { errorCode?: string }; status?: number }
      const rawErrorCode = error?.data?.errorCode ?? 'UNKNOWN'
      const httpStatus = error?.status
      // Normalize HTTP 429 to RATE_LIMITED *before* logging so analytics receives the same
      // semantic error classification the UI uses, instead of falling back to UNKNOWN when the
      // server omits errorCode on rate-limit responses.
      const resolvedError = httpStatus === 429 ? 'RATE_LIMITED' : rawErrorCode
      logEvent(TrackingEvent.PROMO_CODE_APPLY_FAILED, { errorCode: resolvedError, httpStatus, attemptNumber: nextAttempt })
      setLiveMessage(resolveErrorMessage(resolvedError, t))
      setView({ kind: 'expanded', inputValue: view.inputValue, errorCode: resolvedError })
    }
  }

  const handleRemove = async (): Promise<void> => {
    logEvent(TrackingEvent.PRESSED_PROMO_CODE_REMOVE)
    try {
      await updatePromo({ token, request: { action: 'CLEAR' } }).unwrap()
      // Intentionally do NOT clear `manualPromoCode` here. The CLEAR mutation does not update
      // `state.checkout.started.promotion` in the reducer, so for one or more renders after
      // remove, `started.promotion` is still set. If we cleared the marker now, the live
      // `isAutoApplied` gate would see (promotion exists, no marker) and unmount the component
      // mid-flow, stranding the user. Leaving the marker stale is safe: if the user re-applies,
      // the new dispatch overwrites it; if `started.promotion` eventually clears, the gate's
      // `!promotion` branch handles it without consulting the marker.
      setLiveMessage(t('promoCode:removed'))
      setAttemptCount(0)
      setView({ kind: 'collapsed' })
    } catch {
      // removal failure is silent — leave applied state intact
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (view.kind !== 'expanded') return
    setView({ kind: 'expanded', inputValue: e.target.value, errorCode: null })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      void handleApply()
    }
  }

  if (isAutoApplied) {
    // Auto-applied promo at /start — render nothing. The auto-applied promo's UI (e.g. badge in
    // OrderSummary's RenderPromo) handles its own display.
    return null
  }

  if (isLoading) {
    return (
      <div data-testid={`${testNameSpace}-loading`}>
        <Skeleton />
      </div>
    )
  }

  // Render TrackOnMount + the aria-live region once at the root. Previously each view branch
  // had its own copy, which caused remount-on-transition and over-fired VIEWED_PROMO_CODE_INPUT
  // (skewing promo-funnel metrics for users who toggled the control multiple times).
  const expandedContent = view.kind === 'expanded' ? view : null
  const inputValue = expandedContent?.inputValue ?? ''
  const errorCode = expandedContent?.errorCode ?? null
  const hasInput = inputValue.trim().length > 0
  const errorMessage = errorCode ? resolveErrorMessage(errorCode, t) : null
  // Apply must visibly disable while an error is showing so users don't keep firing the same
  // request. handleInputChange clears errorCode on any input edit, so the disabled window is
  // tight to the actual error condition.
  const isApplyDisabled = !hasInput || isAttemptLimitReached || !!errorCode

  return (
    <div className={styles.container} data-testid={testNameSpace}>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO_CODE_INPUT} />
      <div aria-live='polite' aria-atomic='true' className={styles.liveRegion}>
        {liveMessage}
      </div>
      {view.kind === 'collapsed' && (
        <button type='button' className={styles.toggleRow} onClick={handleToggle} aria-expanded={false} data-testid={`${testNameSpace}-toggle`}>
          <Text>
            <Trans i18nKey='promoCode:havePromo' />
          </Text>
          <Icon name='CaretDown' />
        </button>
      )}
      {view.kind === 'applied' && (
        <button type='button' className={styles.appliedRow} onClick={handleToggle} aria-expanded={false} data-testid={`${testNameSpace}-applied-toggle`}>
          <Text>
            <Trans i18nKey='promoCode:codeApplied' />
          </Text>
          <span className={styles.discountAmount}>
            <Text>- {view.discountAmount}</Text>
          </span>
          <Icon name='CaretDown' />
        </button>
      )}
      {view.kind === 'appliedExpanded' && (
        <>
          <button type='button' className={styles.appliedRow} onClick={handleToggle} aria-expanded={true} data-testid={`${testNameSpace}-applied-toggle`}>
            <Text>
              <Trans i18nKey='promoCode:codeApplied' />
            </Text>
            <Icon name='CaretUp' />
          </button>
          <div className={styles.expandedSection}>
            {/* Figma state 5: applied code rendered inside the input container with selected
                border + an inline clear-field X on the right. Static text (not editable) — the X
                is the only way to mutate. */}
            <div className={`${styles.input} ${styles.appliedInput}`} data-testid={`${testNameSpace}-applied-code`}>
              <Text>{view.promoCode}</Text>
              <button
                type='button'
                className={styles.clearFieldButton}
                onClick={() => void handleRemove()}
                data-testid={`${testNameSpace}-remove`}
                aria-label={t('promoCode:remove')}
              >
                <Icon name='Close' />
              </button>
            </div>
            <Text size='S' color='Gray40' data-testid={`${testNameSpace}-applied-helper`}>
              <Trans i18nKey='promoCode:helperText' />
            </Text>
          </div>
        </>
      )}
      {view.kind === 'expanded' && (
        <>
          <button type='button' className={styles.toggleRow} onClick={handleToggle} aria-expanded={true} data-testid={`${testNameSpace}-toggle`}>
            <Text>
              {/* Figma swaps the toggle label between "Afterpay promo code" (state 2, empty input)
                  and "Have an Afterpay promo?" (state 3, has typed content). */}
              <Trans i18nKey={hasInput ? 'promoCode:havePromo' : 'promoCode:label'} />
            </Text>
            <Icon name='CaretUp' />
          </button>
          <div className={styles.expandedSection}>
            <div className={styles.inputRow}>
              {/* Figma state 3 (Promo code entered) shows only input + Apply — no inline
                  clear-field X. Removed in PR-E to match the spec. */}
              <input
                ref={inputRef}
                type='text'
                className={errorCode ? `${styles.input} ${styles.inputError}` : styles.input}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={t('promoCode:placeholder')}
                aria-label={t('promoCode:label')}
                aria-describedby={errorCode ? errorId : undefined}
                aria-invalid={!!errorCode}
                disabled={isAttemptLimitReached}
                data-testid={`${testNameSpace}-input`}
              />
              <Button kind='Secondary' disabled={isApplyDisabled} onClick={() => void handleApply()} data-testid={`${testNameSpace}-apply`}>
                <Trans i18nKey='promoCode:apply' />
              </Button>
            </div>
            {isAttemptLimitReached ? (
              <div className={styles.errorRow} role='alert' id={errorId}>
                <Icon name='Warning' size='Small' color='Error' />
                <Text size='S' color='Error'>
                  <Trans i18nKey='promoCode:tooManyAttempts' />
                </Text>
              </div>
            ) : errorMessage ? (
              <div className={styles.errorRow} role='alert' id={errorId} data-testid={`${testNameSpace}-error`}>
                <Icon name='Warning' size='Small' color='Error' />
                <Text size='S' color='Error'>
                  {errorMessage}
                </Text>
              </div>
            ) : (
              <Text size='S' color='Gray40' data-testid={`${testNameSpace}-helper`}>
                <Trans i18nKey='promoCode:helperText' />
              </Text>
            )}
          </div>
        </>
      )}
    </div>
  )
}
